import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import {
  PersistQueryClientProvider,
  removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { IconoirProvider } from 'iconoir-react';

import App from './App';
import { AuthContextProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BrandingContextProvider } from './contexts/BrandingContext';
import { SnackbarContextProvider } from './contexts/SnackbarContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { UserContextProvider } from './contexts/UserContext';
import { SuperAgentPreferencesProvider } from './contexts/SuperAgentPreferencesContext';
import { WebsiteUpdaterContextProvider } from './contexts/WebsiteUpdaterContext';
import AppErrorBoundary, {
  reloadForStaleDeploy,
} from './components/Common/AppErrorBoundary';
import { startFreezeMonitor } from './utils/freeze-monitor';
import './index.css';
import './utils/themeInit';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { AuditLogContextProvider } from './contexts/AuditLogContext';
import { serializeQueryCache } from './utils/queryCache';

const EXTENSION_PROTOCOL = /^(chrome|moz|safari-web)-extension:\/\//;

const hasExtensionFrame = (event: Sentry.ErrorEvent): boolean =>
  event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some((frame) =>
      EXTENSION_PROTOCOL.test(frame.filename ?? ''),
    ),
  ) ?? false;

// Noise that is not worth reporting from either pipeline.
const IGNORED_MESSAGES = [
  // Stale-chunk errors after a deploy are handled by the vite:preloadError
  // reload below — they are not bugs.
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
  // Request cancelled when the user navigates away — benign.
  /signal is aborted without reason/,
  /The user aborted a request/,
  // Transient connectivity blips during Firebase auth — not app bugs.
  /auth\/network-request-failed/,
  // iframe-resizer library posts non-JSON messages our preview handler
  // ignores — the parse failure is harmless noise.
  /\[iFrameResi/,
];

// Logs are a separate pipeline from errors, so `ignoreErrors` does not apply
// to them and the same noise re-enters through `console.error` unless it is
// filtered again here.
const IGNORED_LOG_MESSAGES = [
  ...IGNORED_MESSAGES,
  // Third-party SDKs warning about how we call them: one per analytics event,
  // and nothing we would act on.
  /\[Meta Pixel\]/,
];

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  // Structured, queryable logs. Errors we already handle are reported with
  // `console.error` plus a friendly message, so today they leave no trace in
  // production; as logs they become searchable without touching a call site.
  enableLogs: true,
  integrations: [
    Sentry.replayIntegration(),
    // `log` and `debug` are development noise and stay out.
    Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
    // Captures Web Vitals (INP, LCP, CLS), route navigation timings and long
    // animation frames, keyed by parameterised route name. Requires
    // `wrapReactRouterRouting` around <Routes> in App.tsx.
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      // Attributes a slow frame to the script that blocked it. Off by default.
      enableLongAnimationFrame: true,
    }),
    // Function-level flame graphs from real sessions, so a slow frame can be
    // traced to the component that caused it rather than just to React's
    // scheduler. Chromium-only, and needs the `Document-Policy: js-profiling`
    // response header set on the dashboard hosting target in firebase.json.
    Sentry.browserProfilingIntegration(),
  ],
  // Profile while a sampled root span is open, so profiling covers page loads
  // and navigations and stops in between rather than running continuously.
  profileLifecycle: 'trace',
  // Every session, for now — this is scoped to the page-editor investigation
  // (SUP-596) and should come down once that has an answer.
  profileSessionSampleRate: 1.0,
  // At ~140 sessions/day this is an estimated 300–600k spans/month, against
  // ~1.9M of monthly span headroom (see SUP-462). Revisit once we have a
  // baseline — the rate can drop a lot without losing the signal.
  tracesSampleRate: 0.5,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 0.5,
  ignoreErrors: IGNORED_MESSAGES,
  // Errors injected by browser extensions are not our bugs.
  denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//],
  beforeSend: (event) => (hasExtensionFrame(event) ? null : event),
  beforeSendLog: (log) =>
    IGNORED_LOG_MESSAGES.some((pattern) => pattern.test(log.message))
      ? null
      : log,
});

startFreezeMonitor();

// A chunk that will not load means the deploy this tab booted from is gone.
// Vite rethrows unless the event is cancelled, and the rethrow is what used to
// surface React Router's default error screen before the reload could happen.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadForStaleDeploy();
});

// import Hotjar from '@hotjar/browser';

// Initiate Hotjar only in prod

// const siteId = 0;
// const hotjarVersion = 6;
// Hotjar.init(siteId, hotjarVersion, {
//   debug: import.meta.env.NODE_ENV === 'development',
// });

if (import.meta.env.VITE_CLARITY_PROJECT_ID) {
  Clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
    dehydrate: {
      // Only persist successful queries that haven't opted out.
      // - Skip pending/error queries so they don't rehydrate as stale promises
      // - Skip queries with meta.persist: false (non-serializable data like Firestore snapshots)
      shouldDehydrateQuery: (query) =>
        query.state.status === 'success' && query.meta?.persist !== false,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: 'SUPERFUNNEL_QUERY_CACHE',
  serialize: serializeQueryCache,
  retry: removeOldestQuery,
});

const router = createBrowserRouter([
  {
    path: '*',
    errorElement: <AppErrorBoundary />,
    element: (
      <SnackbarContextProvider>
        <AuthContextProvider>
          <NotificationProvider>
            <SystemConfigProvider>
              <UserContextProvider>
                <AnalyticsProvider>
                  <AuditLogContextProvider>
                    <WebsiteUpdaterContextProvider>
                      <ThemeContextProvider>
                        <BrandingContextProvider>
                          <IconoirProvider>
                            <SuperAgentPreferencesProvider>
                              <App />
                            </SuperAgentPreferencesProvider>
                          </IconoirProvider>
                        </BrandingContextProvider>
                      </ThemeContextProvider>
                    </WebsiteUpdaterContextProvider>
                  </AuditLogContextProvider>
                </AnalyticsProvider>
              </UserContextProvider>
            </SystemConfigProvider>
          </NotificationProvider>
        </AuthContextProvider>
      </SnackbarContextProvider>
    ),
  },
]);

root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
      }}
    >
      <RouterProvider router={router} />
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
